import { normalizeCartPayload } from './cart-utils.mjs';

// Give React-rendered summary rows a stable identity without depending on their order.
window.wc?.blocksCheckout?.registerCheckoutFilters('seluvia-checkout-product-links', {
    cartItemClass: (value, _extensions, args) => {
        const key = String(args?.cartItem?.key || '');
        return /^[a-zA-Z0-9_-]+$/.test(key) ? `${value} seluvia-cart-item-${key}` : value;
    },
});

const createElement = (tagName, className, text) => {
    const element = document.createElement(tagName);

    if (className) {
        element.className = className;
    }

    if (text !== undefined && text !== null) {
        element.textContent = String(text);
    }

    return element;
};

const parseMoney = (value) => {
    const normalized = String(value || '')
        .replace(/\s+/g, '')
        .replace(/[^0-9,-]/g, '')
        .replace(',', '.');
    const amount = Number(normalized);

    return Number.isFinite(amount) ? amount : 0;
};

const formatPln = (amount) => `${new Intl.NumberFormat('pl-PL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
}).format(amount)} zł`;

const parseInitialCart = (root) => {
    const source = root.querySelector('[data-checkout-cart-initial]');

    if (!source?.textContent) {
        return null;
    }

    try {
        return normalizeCartPayload(JSON.parse(source.textContent));
    } catch {
        return null;
    }
};

const checkoutHasFreeShipping = (page) => {
    const selectedMethod = page.querySelector(
        '.wc-block-checkout__shipping-option input[type="radio"]:checked',
    )?.closest(
        '.wc-block-components-radio-control__option, .wc-block-components-radio-control-accordion-option',
    );
    const shippingTotal = page.querySelector(
        '.wp-block-woocommerce-checkout-order-summary-shipping-block .wc-block-components-totals-item__value',
    );
    const text = `${selectedMethod?.textContent || ''} ${shippingTotal?.textContent || ''}`
        .replace(/\s+/g, ' ')
        .trim()
        .toLocaleLowerCase('pl');

    return text.includes('bezpłatnie')
        || text.includes('za darmo')
        || text.includes('darmowa wysyłka')
        || /(^|\s)0[,.]00\s*zł/.test(text);
};

const setText = (element, value) => {
    if (element && element.textContent !== value) {
        element.textContent = value;
    }
};

const enhanceProductLinks = (item, product) => {
    const value = product?.permalink || product?.url;
    if (!value) return;
    let url;
    try {
        url = new URL(value, window.location.origin);
        if (url.origin !== window.location.origin || !['http:', 'https:'].includes(url.protocol)) return;
    } catch { return; }

    const title = item.querySelector('.wc-block-components-product-name');
    const label = `Zobacz produkt: ${title?.textContent?.trim() || product.name || 'Produkt'}`;
    [title, item.querySelector('.wc-block-components-order-summary-item__image')].forEach((target) => {
        if (!target) return;
        if (target.tagName === 'A') {
            target.href = url.href;
            return;
        }
        // Overlay links leave React-owned images and title children in place.
        let link = target.querySelector(':scope > .CheckoutProductLink');
        if (!link) {
            link = createElement('a', 'CheckoutProductLink');
            target.append(link);
        }
        link.href = url.href;
        link.setAttribute('aria-label', label);
    });
};

const enhanceOrderSummaryItems = (page, cart) => {
    let products = cart?.items || [];
    try {
        const liveItems = window.wp?.data?.select('wc/store/cart')?.getCartData()?.items;
        if (Array.isArray(liveItems) && liveItems.length) products = liveItems;
    } catch { /* The server snapshot also supports the initial checkout render. */ }

    page.querySelectorAll('.wc-block-components-order-summary-item').forEach((item, index) => {
        const identity = [...item.classList].find((name) => name.startsWith('seluvia-cart-item-'));
        const product = identity
            ? products.find((candidate) => candidate.key === identity.slice('seluvia-cart-item-'.length))
            : products[index];
        if (product) enhanceProductLinks(item, product);
        const image = item.querySelector('.wc-block-components-order-summary-item__image img');
        const source = item.querySelector(
            '.wc-block-components-order-summary-item__total-price '
            + '.wc-block-cart-item__total-price-and-sale-badge-wrapper',
        );
        const currentText = source?.querySelector(
            ':scope > .wc-block-components-product-price .wc-block-components-product-price__value',
        )?.textContent?.trim() || '';
        const savingText = source?.querySelector(
            '.wc-block-components-sale-badge .wc-block-formatted-money-amount',
        )?.textContent?.trim() || '';

        if (image) {
            const responsiveSizes = '(max-width: 620px) 68px, 80px';

            if (image.sizes !== responsiveSizes) {
                image.sizes = responsiveSizes;
            }
        }

        if (!source || !currentText) {
            return;
        }

        let price = item.querySelector(':scope > .CheckoutSummaryItemPrice');

        if (!price) {
            price = createElement('div', 'CheckoutSummaryItemPrice');
            price.setAttribute('aria-hidden', 'true');
            price.append(
                createElement('span', 'CheckoutSummaryItemPrice__current'),
                createElement('del', 'CheckoutSummaryItemPrice__regular'),
                createElement('span', 'CheckoutSummaryItemPrice__saving'),
            );
            item.append(price);
        }

        const current = price.querySelector('.CheckoutSummaryItemPrice__current');
        const regular = price.querySelector('.CheckoutSummaryItemPrice__regular');
        const saving = price.querySelector('.CheckoutSummaryItemPrice__saving');
        const currentAmount = parseMoney(currentText);
        const savingAmount = parseMoney(savingText);

        setText(current, currentText);

        if (savingAmount > 0 && currentAmount > 0) {
            const regularText = formatPln(currentAmount + savingAmount);
            setText(regular, regularText);
            setText(saving, `Oszcz. ${savingText}`);
            regular.hidden = false;
            saving.hidden = false;
            saving.title = `Oszczędzasz ${savingText}`;
            price.classList.add('has-discount');
        } else {
            regular.hidden = true;
            saving.hidden = true;
            saving.removeAttribute('title');
            price.classList.remove('has-discount');
        }

        item.classList.add('is-checkout-enhanced');
    });
};

const enhanceCoupon = (scope, checkoutPage) => {
    const coupon = scope.querySelector('.wc-block-components-totals-coupon');
    const toggle = coupon?.querySelector('.wc-block-components-panel__button');
    const input = coupon?.querySelector('.wc-block-components-totals-coupon__input input');

    if (!coupon || !toggle) {
        return;
    }

    const textNode = [...toggle.childNodes].find((node) => (
        node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== ''
    ));

    if (textNode && textNode.textContent.trim() !== 'Kod rabatowy') {
        textNode.textContent = 'Kod rabatowy';
    }

    if (checkoutPage.dataset.checkoutCouponDefaultOpen !== 'true') {
        checkoutPage.dataset.checkoutCouponDefaultOpen = 'true';

        if (toggle.getAttribute('aria-expanded') !== 'true') {
            toggle.click();
        }
    }

    toggle.setAttribute(
        'aria-label',
        toggle.getAttribute('aria-expanded') === 'true'
            ? 'Ukryj pole kodu rabatowego'
            : 'Dodaj kod rabatowy',
    );
    coupon.classList.add('is-checkout-enhanced');

    if (input) {
        input.placeholder = 'Wpisz kod rabatowy';
        input.setAttribute('aria-label', 'Kod rabatowy');
    }
};

const enforceShippingAddressForBilling = (page) => {
    const checkbox = page.querySelector(
        '.wc-block-checkout__use-address-for-billing input[type="checkbox"]',
    );

    if (!checkbox) {
        return;
    }

    page.classList.add('is-shipping-used-for-billing');

    if (!checkbox.checked && page.dataset.checkoutBillingSyncRequested !== 'true') {
        page.dataset.checkoutBillingSyncRequested = 'true';
        checkbox.click();
    }
};

const groupContactAndShippingFields = (page) => {
    const form = page.querySelector('.wc-block-checkout__form');
    const contact = form?.querySelector('#contact-fields');
    const shipping = form?.querySelector('#shipping-fields');

    if (!form || !contact || !shipping) {
        return;
    }

    form.classList.add('is-contact-shipping-grouped');
    contact.classList.add('CheckoutContactGroup__contact');
    shipping.classList.add('CheckoutContactGroup__shipping');

    const contactLegend = contact.querySelector(':scope > legend');
    const shippingTitle = shipping.querySelector(
        '.wc-block-components-checkout-step__title',
    );

    setText(contactLegend, 'Dane kontaktowe i adres do wysyłki');

    if (shippingTitle) {
        shippingTitle.setAttribute('role', 'heading');
        shippingTitle.setAttribute('aria-level', '3');
    }
};

const enhanceAccountCreation = (page) => {
    const option = page.querySelector('.wc-block-checkout__create-account');
    const checkbox = option?.querySelector('input[type="checkbox"]');
    const label = option?.querySelector('.wc-block-components-checkbox__label');

    if (!option || !checkbox) {
        return;
    }

    option.classList.add('is-checkout-enhanced');

    if (label) {
        setText(label, 'Utwórz konto');
    }

    let benefit = option.querySelector('[data-checkout-account-benefit]');

    if (!benefit) {
        benefit = createElement(
            'p',
            'CheckoutAccountChoice__benefit',
            'Śledź zamówienia i kupuj szybciej przy kolejnej wizycie.',
        );
        benefit.dataset.checkoutAccountBenefit = '';
        benefit.id = 'checkout-account-benefit';
        option.append(benefit);
    }

    const describedBy = new Set(
        (checkbox.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean),
    );
    describedBy.add(benefit.id);
    checkbox.setAttribute('aria-describedby', [...describedBy].join(' '));
};

const enhanceSummaryTotals = (scope, page) => {
    const subtotalLabels = scope.querySelectorAll(
        '.wp-block-woocommerce-checkout-order-summary-subtotal-block '
        + '.wc-block-components-totals-item__label',
    );
    const shippingBlocks = scope.querySelectorAll(
        '.wp-block-woocommerce-checkout-order-summary-shipping-block',
    );
    const shippingIsFree = checkoutHasFreeShipping(page);
    const installmentWrappers = scope.querySelectorAll(
        '.wc-block-components-totals-wrapper.slot-wrapper',
    );

    subtotalLabels.forEach((label) => setText(label, 'Suma produktów'));
    shippingBlocks.forEach((shippingBlock) => {
        const shippingLabel = shippingBlock.querySelector(
            '.wc-block-components-totals-item__label',
        );
        const shippingValue = shippingBlock.querySelector(
            '.wc-block-components-totals-item__value',
        );
        const shippingDescription = shippingBlock.querySelector(
            '.wc-block-components-totals-item__description',
        );
        const descriptionText = shippingDescription?.textContent
            ?.replace(/\s+/g, ' ').trim().toLocaleLowerCase('pl') || '';

        setText(shippingLabel, 'Dostawa');

        if (shippingIsFree) {
            setText(shippingValue, 'Bezpłatnie');
        }

        shippingBlock.classList.toggle('is-checkout-free-shipping', shippingIsFree);
        shippingDescription?.classList.toggle(
            'is-checkout-redundant-shipping',
            (shippingIsFree && ['darmowa wysyłka', 'darmowa dostawa', 'free shipping'].includes(descriptionText))
                || /^paczkomaty\s+inpost$/i.test(descriptionText),
        );
    });

    installmentWrappers.forEach((wrapper) => {
        const text = wrapper.textContent?.replace(/\s+/g, ' ').trim().toLocaleLowerCase('pl') || '';
        const isInstallmentWidget = Boolean(
            wrapper.querySelector('.payu-mini-installments-widget')
            || text.includes('zapłać później'),
        );

        wrapper.classList.toggle('is-checkout-installments-hidden', isInstallmentWidget);

        if (isInstallmentWidget) {
            wrapper.setAttribute('aria-hidden', 'true');
        } else {
            wrapper.removeAttribute('aria-hidden');
        }
    });
};

const renderSummaryExtras = (page, payload) => {
    const summaryRoot = page.querySelector('.wc-block-checkout__sidebar') || page;
    const coupon = summaryRoot.querySelector('.wc-block-components-totals-coupon');
    let choice = coupon?.querySelector('[data-bogo-coupon-choice]');
    const choiceMessage = String(payload.promotion_choice?.message || '');
    if (coupon && choiceMessage) {
        if (!choice) {
            choice = createElement('p', 'CheckoutCouponChoice');
            choice.dataset.bogoCouponChoice = '';
            choice.setAttribute('aria-live', 'polite');
            coupon.append(choice);
        }
        setText(choice, choiceMessage);
    } else {
        choice?.remove();
    }
    const summaryContent = summaryRoot.querySelector(
        '.wc-block-components-checkout-order-summary__content',
    );
    const totalWrapper = summaryRoot.querySelector(
        '.wc-block-components-totals-footer-item',
    )?.closest('.wc-block-components-totals-wrapper');
    let extras = summaryRoot.querySelector('[data-checkout-summary-extras]');
    const shippingIsFree = checkoutHasFreeShipping(page);
    const progressSource = payload.shipping_progress || {};
    const progress = shippingIsFree
        ? {
            ...progressSource,
            enabled: true,
            complete: true,
            percent: 100,
            message: 'Darmowa dostawa',
            caption: '0 zł',
        }
        : progressSource;
    const savings = payload.summary?.savings || {};
    const showShippingProgress = Boolean(progress.enabled && !progress.complete);
    const showSavings = Boolean(savings.visible && savings.formatted);

    if (!summaryContent || (!showShippingProgress && !showSavings)) {
        extras?.remove();
        return;
    }

    if (!extras) {
        extras = createElement('div', 'CheckoutSummaryExtras');
        extras.dataset.checkoutSummaryExtras = '';
        extras.setAttribute('aria-live', 'polite');
    }

    if (totalWrapper && extras.nextElementSibling !== totalWrapper) {
        totalWrapper.insertAdjacentElement('beforebegin', extras);
    } else if (!extras.isConnected) {
        summaryContent.append(extras);
    }

    let shipping = extras.querySelector('.CheckoutSummaryExtras__shipping');

    if (showShippingProgress && !shipping) {
        shipping = createElement(
            'section',
            'CheckoutSummaryExtras__shipping',
        );
        const head = createElement('div', 'CheckoutSummaryExtras__shippingHead');
        const icon = createElement('span', 'CheckoutSummaryExtras__shippingIcon');
        icon.setAttribute('aria-hidden', 'true');
        icon.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h11v11H3zM14 10h4l3 4v3h-7"/><circle cx="7" cy="18" r="2" fill="var(--checkout-soft)"/><circle cx="18" cy="18" r="2" fill="var(--checkout-soft)"/></svg>';
        const copy = createElement('span', 'CheckoutSummaryExtras__shippingCopy');
        const message = createElement('span', 'CheckoutSummaryExtras__shippingMessage');
        message.append(
            createElement('span', 'CheckoutSummaryExtras__shippingPrefix'),
            createElement('strong', 'CheckoutSummaryExtras__shippingAmount'),
            createElement('span', 'CheckoutSummaryExtras__shippingSuffix'),
        );
        copy.append(
            message,
            createElement('span', 'CheckoutSummaryExtras__shippingCaption'),
        );
        head.append(
            icon,
            copy,
        );

        const track = createElement('span', 'CheckoutSummaryExtras__track');
        const bar = createElement('span', 'CheckoutSummaryExtras__bar');
        track.setAttribute('aria-hidden', 'true');
        track.append(bar);
        shipping.append(head, track);
        extras.prepend(shipping);
    }

    if (showShippingProgress) {
        const remaining = Number(progress.remaining);
        const hasAmount = Number.isFinite(remaining) && remaining >= 0;
        setText(shipping.querySelector('.CheckoutSummaryExtras__shippingPrefix'), hasAmount ? 'Jeszcze ' : progress.message);
        setText(shipping.querySelector('.CheckoutSummaryExtras__shippingAmount'), hasAmount ? formatPln(remaining) : '');
        setText(shipping.querySelector('.CheckoutSummaryExtras__shippingSuffix'), hasAmount ? ' do darmowej dostawy' : '');
        setText(shipping.querySelector('.CheckoutSummaryExtras__shippingCaption'), progress.caption);
        const percent = Math.min(100, Math.max(0, Number(progress.percent) || 0));
        // Keep the same bar between cart updates so its transition can finish.
        shipping.querySelector('.CheckoutSummaryExtras__bar').style.setProperty('--checkout-shipping-progress', `${percent}%`);
    } else {
        shipping?.remove();
    }

    let saving = extras.querySelector('.CheckoutSummaryExtras__saving');

    if (showSavings && !saving) {
        saving = createElement('div', 'CheckoutSummaryExtras__saving');
        saving.append(
            createElement('span', 'CheckoutSummaryExtras__savingLabel', 'Oszczędzasz'),
            createElement('span', 'CheckoutSummaryExtras__savingValue'),
        );
        extras.append(saving);
    }

    if (showSavings) {
        setText(saving.querySelector('.CheckoutSummaryExtras__savingValue'), savings.formatted);
    } else {
        saving?.remove();
    }
};

const findFirstInvalidControl = (page) => {
    const explicitlyInvalid = page.querySelector(
        'input[aria-invalid="true"], select[aria-invalid="true"], textarea[aria-invalid="true"]',
    );

    if (explicitlyInvalid) {
        return explicitlyInvalid;
    }

    const validation = page.querySelector('.wc-block-components-validation-error');

    return validation?.closest(
        '.wc-block-components-text-input, .wc-blocks-components-select, .wc-block-components-combobox',
    )?.querySelector('input, select, textarea') || null;
};

const focusInvalidControl = (page) => {
    const control = findFirstInvalidControl(page);

    if (!control) {
        return false;
    }

    control.scrollIntoView({ behavior: 'smooth', block: 'center' });

    window.setTimeout(() => {
        try {
            control.focus({ preventScroll: true });
        } catch {
            control.focus();
        }
    }, 320);

    return true;
};

export const initCheckout = (root) => {
    if (root.dataset.checkoutReady === 'true') {
        return;
    }

    const page = root.querySelector('.CheckoutPage');

    if (!page) {
        return;
    }

    const mobileQuery = window.matchMedia('(max-width: 960px)');
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const endpoint = String(root.dataset.cartEndpoint || '');
    let currentCart = parseInitialCart(root);
    let summaryOpen = false;
    let refreshTimer = 0;
    let enhanceFrame = 0;
    let validationDeadline = 0;
    let refreshController = null;

    const setSummaryOpen = (open) => {
        summaryOpen = Boolean(open);
        page.classList.toggle('is-summary-open', summaryOpen);

        const title = page.querySelector(
            '.wc-block-components-checkout-order-summary__title',
        );
        const total = title?.querySelector(
            '.wc-block-components-checkout-order-summary__title-price',
        )?.textContent?.trim();

        if (title && mobileQuery.matches) {
            title.setAttribute('aria-expanded', String(summaryOpen));
            title.setAttribute(
                'aria-label',
                `${summaryOpen ? 'Ukryj' : 'Pokaż'} podsumowanie zamówienia${total ? `. Łącznie ${total}` : ''}`,
            );
        }
    };

    const enhanceSummary = () => {
        enforceShippingAddressForBilling(page);
        groupContactAndShippingFields(page);
        enhanceAccountCreation(page);

        const title = page.querySelector(
            '.wc-block-components-checkout-order-summary__title',
        );
        const content = page.querySelector(
            '.wc-block-components-checkout-order-summary__content',
        );
        const summaryRoot = page.querySelector('.wc-block-checkout__sidebar') || page;

        if (!title || !content) {
            return;
        }

        if (!content.id) {
            content.id = 'checkout-order-summary-content';
        }

        if (title.dataset.checkoutToggleBound !== 'true') {
            title.addEventListener('click', () => {
                if (mobileQuery.matches) {
                    setSummaryOpen(!summaryOpen);
                    scheduleEnhancement();
                }
            });
            title.addEventListener('keydown', (event) => {
                if (
                    mobileQuery.matches
                    && (event.key === 'Enter' || event.key === ' ')
                ) {
                    event.preventDefault();
                    setSummaryOpen(!summaryOpen);
                    scheduleEnhancement();
                }
            });
            title.dataset.checkoutToggleBound = 'true';
        }

        page.classList.add('is-summary-enhanced');

        if (mobileQuery.matches) {
            title.setAttribute('role', 'button');
            title.tabIndex = 0;
            // The coupon stays visible independently of the collapsible details.
            const details = [...content.children].filter((element) => !element.matches(
                '.wp-block-woocommerce-checkout-order-summary-coupon-form-block',
            ));
            details.forEach((element, index) => {
                if (!element.id) element.id = `checkout-summary-detail-${index}`;
            });
            title.setAttribute('aria-controls', details.map((element) => element.id).join(' '));
            setSummaryOpen(summaryOpen);
        } else {
            title.removeAttribute('role');
            title.removeAttribute('tabindex');
            title.removeAttribute('aria-controls');
            title.removeAttribute('aria-expanded');
            title.removeAttribute('aria-label');
            page.classList.remove('is-summary-open');
        }

        enhanceOrderSummaryItems(page, currentCart);
        enhanceCoupon(summaryRoot, page);
        enhanceSummaryTotals(summaryRoot, page);
        renderSummaryExtras(page, currentCart);
    };

    const scheduleEnhancement = () => {
        window.cancelAnimationFrame(enhanceFrame);
        enhanceFrame = window.requestAnimationFrame(enhanceSummary);
    };

    const refreshCart = async () => {
        if (!endpoint) {
            return;
        }

        refreshController?.abort();
        refreshController = new AbortController();

        try {
            const response = await fetch(endpoint, {
                credentials: 'same-origin',
                cache: 'no-store',
                headers: { Accept: 'application/json' },
                signal: refreshController.signal,
            });

            if (!response.ok) {
                return;
            }

            currentCart = normalizeCartPayload(await response.json());
            enhanceOrderSummaryItems(page, currentCart);
            renderSummaryExtras(page, currentCart);
        } catch (error) {
            if (error?.name !== 'AbortError') {
                // Checkout remains fully functional when the optional summary request fails.
            }
        }
    };

    const scheduleCartRefresh = (delay = 700) => {
        window.clearTimeout(refreshTimer);
        refreshTimer = window.setTimeout(refreshCart, delay);
    };

    const observer = new MutationObserver((mutations) => {
        const relevantMutation = mutations.some((mutation) => {
            const target = mutation.target;

            return !(
                target instanceof Element
                && target.closest('[data-checkout-summary-extras]')
            );
        });

        if (!relevantMutation) {
            return;
        }

        scheduleEnhancement();
        scheduleCartRefresh();

        if (validationDeadline > Date.now() && focusInvalidControl(page)) {
            validationDeadline = 0;
        }
    });

    observer.observe(page, { childList: true, subtree: true });

    page.addEventListener('click', (event) => {
        const button = event.target instanceof Element
            ? event.target.closest('.wc-block-components-checkout-place-order-button')
            : null;

        if (!button || button.matches(':disabled, [aria-disabled="true"]')) {
            return;
        }

        try {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ event: 'wc_payment_intent' });
        } catch {
            // Analytics must not interrupt checkout or its validation feedback.
        }

        validationDeadline = Date.now() + 8000;
        window.setTimeout(() => {
            if (focusInvalidControl(page)) {
                validationDeadline = 0;
            }
        }, reducedMotionQuery.matches ? 0 : 180);
    }, true);

    const handleViewportChange = () => {
        if (mobileQuery.matches) {
            setSummaryOpen(false);
        }

        enhanceSummary();
    };

    mobileQuery.addEventListener?.('change', handleViewportChange);
    document.body.addEventListener('wc-blocks_added_to_cart', () => scheduleCartRefresh(100));
    document.body.addEventListener('wc-blocks_removed_from_cart', () => scheduleCartRefresh(100));
    window.addEventListener('pageshow', () => scheduleCartRefresh(100));

    enhanceSummary();
    scheduleCartRefresh(250);
    root.dataset.checkoutReady = 'true';
};
